import React, { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { ReportWrapper } from './styles';
import { ColDef, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { ICenterBillingRecord, IInvoiceCreditPaymentsReport } from '@nkr/models/index';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';

interface IProps {
    data: IInvoiceCreditPaymentsReport[],
    centerId: number,
    onViewClicked: (invoiceNumber: number) => void,
    onGridReady: (e: GridReadyEvent) => void
}

const formatCurrency = (value: number) => {
    if (value == null) return '';
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const Grid = (props: IProps) => {
    const onGridReady = (e: GridReadyEvent) => {
        props.onGridReady(e);
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const getTotals = (data: IInvoiceCreditPaymentsReport[]) => {
        if (!data || data.length == 0) return [];

        return [{
            invoiceNumber: 'Totals',
            invoiceAmount: _.sumBy(data, x => x.invoiceAmount ?? 0),
            amountPaid: _.sumBy(data, x => x.amountPaid ?? 0),
            amountRemaining: _.sumBy(data, x => x.amountRemaining ?? 0),
            numberOfDaysToPay: null
        }];
    };


    const handleView = (invoiceNumber: number) => {
        props.onViewClicked(invoiceNumber);
    };

    const columns: ColDef[] = [
        {
            colId: 'invoiceNumber',
            field: 'invoiceNumber',
            headerName: 'Invoice',
            headerComponentFramework: (p: IHeaderParams) => (
                <SortableHeaderComponent {...p}>Invoice</SortableHeaderComponent>
            ),
            width: 130,
            cellRendererFramework: (p: ICellRendererParams) => {
                if (!p.value || p.node.rowPinned) {
                    return <span>{p.value}</span>;
                }

                return (
                    <button
                        type="button"
                        className="btn btn-link p-0"
                        onClick={() => handleView(p.value)}
                    >
                        {p.value}
                    </button>
                );
            }
        },
        {
            colId: 'invoiceAmount',
            field: 'invoiceAmount',
            headerName: 'Amount Invoiced',
            headerComponentFramework: (p: IHeaderParams) => <SortableHeaderComponent {...p}>Amount Invoiced</SortableHeaderComponent>,
            width: 140,
            cellRenderer: (p: ICellRendererParams) => formatCurrency(p.value)
        },
        {
            colId: 'amountPaid',
            field: 'amountPaid',
            headerName: 'Amount Paid',
            headerComponentFramework: (p: IHeaderParams) => <SortableHeaderComponent {...p}>Amount Paid</SortableHeaderComponent>,
            width: 130,
            cellRenderer: (p: ICellRendererParams) => formatCurrency(p.value)
        },
        {
            colId: 'amountRemaining',
            field: 'amountRemaining',
            headerName: 'Balance Due',
            headerComponentFramework: (p: IHeaderParams) => <SortableHeaderComponent {...p}>Balance Due</SortableHeaderComponent>,
            width: 130,
            cellRenderer: (p: ICellRendererParams) => formatCurrency(p.value)
        },
        {
            colId: 'numberOfDaysToPay',
            headerName: 'numberOfDaysToPay',
            width: 60,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}># of Days<br />to Pay</SortableHeaderComponent>,
            cellRenderer: (props: ICellRendererParams) => {
                return props.value < 0 ? "--" : props.value.toString();
            },
            valueGetter: (props: ValueGetterParams) => {
                if (props.data != null) {
                    let invoiceDate = moment({ year: props.data.invoiceYear, month: props.data.invoiceMonth - 1, day: 1 }).endOf('month').startOf('day');
                    return props.data.paymentDate == null ? 0 : moment(props.data.paymentDate).diff(invoiceDate, 'days');
                }
                else {
                    return 0;
                }
            }
        }
    ];

    const columnDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => props.data, [props.data]);
    const totals = useMemo(() => getTotals(props.data), [props.data]);

    return (
        <div style={{ width: '60%', minWidth: 480 }}>
            <ReportWrapper className='ag-theme-bootstrap'>
                <AgGridReact
                    columnDefs={columnDefs}
                    defaultColDef={{
                        headerClass: 'list-header',
                        cellClass: 'list-cell-center',
                        sortable: true,
                        resizable: false
                    }}
                    rowData={rowData}
                    headerHeight={46}
                    groupHeaderHeight={30}
                    rowHeight={30}
                    pinnedTopRowData={totals}
                    suppressRowClickSelection={false}
                    suppressCellSelection={true}
                    excelStyles={[
                        {
                            id: 'header',
                            alignment: { horizontal: 'Center' },
                            font: { bold: true }
                        },
                        {
                            id: 'cell',
                            alignment: { horizontal: 'Center' }
                        },
                        {
                            id: 'totalsBackground',
                            interior: {
                                color: '#ADD8E6',
                                pattern: 'Solid',
                                patternColor: 'black'
                            }
                        }
                    ]}
                    noRowsOverlayComponentFramework={() => <div>No records found...</div>}
                    onGridReady={onGridReady}
                    onFirstDataRendered={onFirstDataRendered}
                    onSortChanged={(e: SortChangedEvent) => e.api.refreshCells()}
                    onRowDataChanged={onRowDataChanged}
                    getRowStyle={(p: any) => {
                        if (p.node.rowPinned) {
                            return { backgroundColor: '#d7edf8', fontWeight: 700 } as any;
                        }
                    }}
                />
            </ReportWrapper>
        </div>
    );
};

export default Grid;